import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Bloques, Contador, ListaFacturas, Suscripcion } from '../../api/operaciones';
import { conDosPasosEnElMomento } from '../../api/dosPasosEnElMomento';
import type { PaqueteWhatsApp } from '../../api/ops/plan';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Chip } from '../../componentes/Chip';
import { EstadoCargando, EstadoError, EstadoVacia } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { Paginacion } from '../../componentes/Paginacion';
import { Segmentos } from '../../componentes/Segmentos';
import { Tabla } from '../../componentes/Tabla';
import { Ventana } from '../../componentes/Ventana';
import { estadoDeError } from '../../logica/estados';
import { cifraDeLista, usePaginacionCursor } from '../../logica/paginacion';
import { useCabecera } from '../../marco/cabecera';
import { usePedirCodigoDosPasos } from '../../marco/dosPasos';
import { usePermisos } from '../../permisos/permisos';
import { rutaConApartado, type PropsPantalla } from '../tipos';
import { COBRO_DISPONIBLE, TEXTO_SIN_COBRO } from './cobro';
import {
    APARTADOS,
    AYUDA,
    ESTADO_FACTURA,
    apartadoDe,
    bloquesDePago,
    compromisosDePausa,
    dia,
    diaConAno,
    diaMasDias,
    diaCorto,
    dinero,
    medidores,
    nombreBloque,
    ordenarBloques,
    origenBloque,
    NOTA_PRUEBA_CONSUMO,
    periodo,
    tarjetaPlan,
    type AccionPlan,
} from './logica';
import './plan.css';

/**
 * Plan (1.15), consumo (1.16) y pausa y baja (1.17). Apartados con RV Segmentos (`?apartado=plan|consumo|
 * facturas`, «Facturas N» con `total_count`, G139). Lee `getSubscription`, `listEntitlements`, `getUsage` y
 * `listInvoices` (`plan.ver`). Solo quien tiene `plan.contratar` (el propietario) contrata, paga o da de baja, y
 * `plan.pausar` pausa; el resto lo ve en solo lectura. Todo lo que cobra espera a G92 y a Stripe (`COBRO_DISPONIBLE`):
 * mientras tanto no hay botones que lo llamen. «Cancelar la pausa» de una pausa programada no cobra (G1176): va sin esperar.
 * `?block=<entitlement>` (G120, el «+» de una entrada no contratada)
 * resalta ese bloque.
 */
interface Datos {
    suscripcion: Suscripcion;
    bloques: Bloques;
    contadores: Contador[];
}

type Ventanita = 'contratar' | 'metodo' | 'pausar' | 'baja' | 'reactivar' | 'compromisos' | 'cancelar-pausa' | 'pausa-cancelada' | null;

export function Plan({ ops, negocio, movil, ruta, busqueda, navegar, soloLectura, onSesionCaducada }: PropsPantalla) {
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const pedirCodigo = usePedirCodigoDosPasos();
    const apartado = apartadoDe(busqueda.get('apartado'));
    const resaltado = busqueda.get('block');
    const contrata = permisos.puede('plan.contratar') && !soloLectura;
    const pausa = permisos.puede('plan.pausar') && !soloLectura;

    const [datos, setDatos] = useState<Datos | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [facturas, setFacturas] = useState<ListaFacturas | null>(null);
    const [errorFacturas, setErrorFacturas] = useState<unknown>(null);
    const pag = usePaginacionCursor();
    const [ventana, setVentana] = useState<Ventanita>(null);
    const [ocupado, setOcupado] = useState(false);
    const [vuelta, setVuelta] = useState('');
    // G1034: el propietario elige el día de inicio (hoy por defecto; tras un 409, el primero posible).
    const [inicio, setInicio] = useState(() => hoyLocal());
    const [pendientes, setPendientes] = useState<ReturnType<typeof compromisosDePausa>>(null);
    const [menu, setMenu] = useState(false);
    // Lo que queda en los paquetes de WhatsApp (listWhatsAppPacks, `plan.ver`); sin respuesta, las tarjetas sin esa línea.
    const [paquetes, setPaquetes] = useState<PaqueteWhatsApp[] | null>(null);

    const caducada = useCallback(
        (e: unknown) => {
            if (e instanceof ErrorApi && e.codigo === 'unauthenticated') {
                onSesionCaducada();
                return true;
            }
            return false;
        },
        [onSesionCaducada],
    );

    useEffect(() => {
        let vivo = true;
        Promise.all([ops.getSubscription(negocio), ops.listEntitlements(negocio), ops.getUsage(negocio)])
            .then(([suscripcion, bloques, uso]) => {
                if (vivo) {
                    setDatos({ suscripcion, bloques, contadores: uso.data });
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo && !caducada(e)) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, intento, caducada]);

    useEffect(() => {
        let vivo = true;
        ops.listWhatsAppPacks(negocio)
            .then((r) => { if (vivo) { setPaquetes(r.data); } })
            .catch(() => { if (vivo) { setPaquetes(null); } });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, intento]);

    // Las facturas se piden siempre (su cifra va en la pestaña), página a página con «Anterior / Siguiente» (G121).
    useEffect(() => {
        let vivo = true;
        ops.listInvoices(negocio, { cursor: pag.cursor })
            .then((lista) => {
                if (vivo) {
                    setFacturas(lista);
                    setErrorFacturas(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo && !caducada(e)) {
                    setErrorFacturas(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, pag.cursor, intento, caducada]);

    const recargar = () => {
        setIntento((i) => i + 1);
    };

    /** Llama a la operación; al acabar, cierra la ventana (o pasa a `despues`) y recarga. */
    const ejecutar = (accion: () => Promise<unknown>, despues: Ventanita = null) => {
        setOcupado(true);
        accion()
            .then(() => {
                setVentana(despues);
                recargar();
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.codigo === 'commitments_pending') {
                    const lista = compromisosDePausa(e);
                    setPendientes(lista);
                    if (lista?.desde !== null && lista?.desde !== undefined) {
                        setInicio(lista.desde);
                    }
                    setVentana('compromisos');
                } else if (!caducada(e)) {
                    avisar({ tipo: 'error', texto: e instanceof ErrorApi ? e.message : 'No he podido hacerlo. Prueba de nuevo.' });
                }
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    /** Lo que cobra, pausa o da de baja: solo con el cobro disponible (G92). */
    const hacer = (accion: () => Promise<unknown>) => {
        if (COBRO_DISPONIBLE) {
            ejecutar(accion);
        }
    };

    const irA = (url: string) => {
        window.location.assign(url);
    };

    const accionDe = (a: AccionPlan) => {
        if (a === 'anular-baja') {
            hacer(() => ops.undoCancellation(negocio));
        } else if (a === 'pagar') {
            setVentana('metodo');
        } else {
            setVentana(a);
        }
    };

    const tarjeta = useMemo(() => (datos === null ? null : tarjetaPlan(datos.suscripcion, datos.bloques.entitlements, new Date())), [datos]);
    const lista = useMemo(() => ordenarBloques(datos?.bloques.entitlements ?? []), [datos]);
    const arcos = useMemo(() => medidores(datos?.contadores ?? [], paquetes), [datos, paquetes]);

    // «Comprar más» (1.16) iría como botón principal de Consumo: espera al cobro y a los paquetes de WhatsApp (G92).
    useCabecera({ ayuda: AYUDA[apartado], acciones: [] });

    const cambiarApartado = (i: number) => {
        const nuevo = APARTADOS[i] ?? 'plan';
        navegar(rutaConApartado(ruta, nuevo === 'plan' ? null : nuevo), true);
    };

    const segmentos = (
        <div className="rv-plan__segmentos">
            <Segmentos
                etiqueta="Apartados del plan"
                movil={movil}
                valor={APARTADOS.indexOf(apartado)}
                onCambio={cambiarApartado}
                items={[
                    { etiqueta: 'Plan', icono: 'receipt' },
                    { etiqueta: 'Consumo', icono: 'gauge' },
                    { etiqueta: 'Facturas', icono: 'file-text', cifra: cifraDeLista(facturas) },
                ]}
            />
        </div>
    );

    if (error !== null) {
        return (
            <div className="rv-plan">
                {segmentos}
                <EstadoError seccion="el plan" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={recargar} movil={movil} />
            </div>
        );
    }

    const contenido = (() => {
        if (apartado === 'facturas') {
            if (errorFacturas !== null) {
                return <EstadoError seccion="las facturas" codigoSoporte={estadoDeError(errorFacturas).codigoSoporte} onReintentar={recargar} movil={movil} />;
            }
            if (facturas === null) {
                return <EstadoCargando seccion="las facturas" />;
            }
            if (facturas.data.length === 0 && !pag.hayAnterior) {
                return <EstadoVacia seccion="las facturas" />;
            }
            return (
                <>
                    <Tabla
                        etiqueta="Facturas"
                        movil={movil}
                        columnas={[
                            { clave: 'fecha', titulo: 'Fecha', ancho: '120px' },
                            { clave: 'concepto', titulo: 'Concepto', ancho: 'minmax(0, 3fr)' },
                            { clave: 'importe', titulo: 'Importe', ancho: '120px', alinear: 'derecha' },
                            { clave: 'estado', titulo: 'Estado', ancho: '120px' },
                        ]}
                        filas={facturas.data.map((f) => {
                            const estado = ESTADO_FACTURA[f.status];
                            const chip = estado === null ? null : <Chip estado={estado[1]}>{estado[0]}</Chip>;
                            const concepto = (
                                <span className="rv-plan__concepto">
                                    <strong>{f.description ?? f.number}</strong>
                                    <span>{f.number}</span>
                                </span>
                            );
                            return {
                                clave: f.id,
                                celdas: { fecha: diaCorto(f.period_start), concepto, importe: <strong className="rv-plan__cifra">{dinero(f.total)}</strong>, estado: chip },
                                tarjeta: (
                                    <>
                                        {concepto}
                                        <span className="rv-plan__linea">
                                            {`${diaCorto(f.period_start)} · `}
                                            <strong>{dinero(f.total)}</strong> {chip}
                                        </span>
                                    </>
                                ),
                                acciones:
                                    f.hosted_invoice_url === undefined ? undefined : (
                                        <a
                                            className="rv-plan__descargar"
                                            href={f.hosted_invoice_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            aria-label={`Descargar la factura ${f.number}`}
                                            title={`Descargar la factura ${f.number}`}
                                        >
                                            <Icono nombre="download" />
                                        </a>
                                    ),
                            };
                        })}
                    />
                    <Paginacion
                        movil={movil}
                        hayAnterior={pag.hayAnterior}
                        haySiguiente={facturas.next_cursor !== null}
                        onAnterior={pag.anterior}
                        onSiguiente={() => {
                            pag.siguiente(facturas.next_cursor);
                        }}
                    />
                </>
            );
        }

        if (datos === null || tarjeta === null) {
            return <EstadoCargando seccion="el plan" />;
        }

        if (apartado === 'consumo') {
            // G998, G1036: en la prueba, el periodo de la prueba con sus cupos y la nota de lo que se cuenta sin cortar.
            const enPrueba = datos.suscripcion.status === 'trialing';
            const linea = periodo(datos.contadores, enPrueba ? (datos.suscripcion.trial_ends_at ?? null) : null);
            return (
                <section className="rv-plan__seccion">
                    <div className="rv-plan__titulo">
                        <h3>Este mes</h3>
                        {linea !== null && <span>{linea}</span>}
                    </div>
                    {enPrueba && (
                        <p className="rv-plan__aviso">
                            <Icono nombre="info" />
                            <span>{NOTA_PRUEBA_CONSUMO}</span>
                        </p>
                    )}
                    {arcos.length === 0 ? (
                        <EstadoVacia seccion="el consumo" />
                    ) : (
                        <div className={`rv-plan__arcos${movil ? ' rv-plan__arcos--movil' : ''}`}>
                            {arcos.map((m) => (
                                <div key={m.clave} className={`rv-plan__arco-tarjeta${m.alerta ? ' rv-plan__arco-tarjeta--alerta' : ''}`}>
                                    {m.porcentaje !== null && <Anillo porcentaje={m.porcentaje} alerta={m.alerta} />}
                                    <span className="rv-plan__arco-textos">
                                        <strong>
                                            {m.alerta && <Icono nombre="triangle-alert" />}
                                            {m.etiqueta}
                                        </strong>
                                        <span>{m.deCuanto}</span>
                                        {m.nota !== '' && <span className="rv-plan__sub">{m.nota}</span>}
                                        {m.partes !== undefined && (
                                            <span className="rv-plan__partes">
                                                {m.partes.map((p) => (
                                                    <span key={p.texto} className="rv-plan__parte">
                                                        <span>{p.texto}</span>
                                                        {p.valor !== '' && <strong>{p.valor}</strong>}
                                                    </span>
                                                ))}
                                            </span>
                                        )}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            );
        }

        const n = bloquesDePago(datos.bloques.entitlements).length;
        const opcionesMenu = tarjeta.menu.filter((m) => (m === 'pausar' ? pausa : contrata));
        const conMenu = COBRO_DISPONIBLE && opcionesMenu.length > 0;
        // «Cancelar la pausa» (G1176) solo pide `plan.pausar`; reactivar, también `plan.contratar`; el resto, `plan.contratar`.
        // Provisional: «Cancelar la pausa» no espera al cobro (G92) porque no cobra nada y `resumeSubscription` ya la anula.
        const accionesTarjeta = tarjeta.acciones.filter((a) =>
            a.accion === 'cancelar-pausa' ? pausa : COBRO_DISPONIBLE && contrata && (a.accion !== 'reactivar' || pausa),
        );
        return (
            <>
                <div className="rv-plan__tarjeta">
                    <div className="rv-plan__cabeza">
                        <div className="rv-plan__principal">
                            {tarjeta.chip !== null && <Chip estado={tarjeta.chip.estado}>{tarjeta.chip.texto}</Chip>}
                            <h2>{tarjeta.titulo}</h2>
                            <p>{tarjeta.texto}</p>
                        </div>
                        {(accionesTarjeta.length > 0 || conMenu) && (
                            <div className="rv-plan__lado">
                                {accionesTarjeta.map((a) => (
                                    <Boton
                                        key={a.accion}
                                        tipo={a.secundaria === true ? 'secundario' : 'principal'}
                                        icono={a.icono}
                                        movil={movil}
                                        onClick={() => {
                                            accionDe(a.accion);
                                        }}
                                    >
                                        {a.texto}
                                    </Boton>
                                ))}
                                {conMenu && (
                                    <div className="rv-plan__menu">
                                        <button
                                            type="button"
                                            className="rv-bi rv-plan__menu-b"
                                            aria-label="Más acciones del plan"
                                            aria-expanded={menu}
                                            onClick={() => {
                                                setMenu((m) => !m);
                                            }}
                                        >
                                            <Icono nombre="ellipsis" />
                                        </button>
                                        {menu && (
                                            <div role="menu" className="rv-plan__menu-lista">
                                                {opcionesMenu.map((m) => (
                                                    <button
                                                        key={m}
                                                        type="button"
                                                        role="menuitem"
                                                        className={m === 'baja' ? 'rv-plan__peligro' : undefined}
                                                        onClick={() => {
                                                            setMenu(false);
                                                            setVentana(m);
                                                        }}
                                                    >
                                                        <Icono nombre={m === 'metodo' ? 'credit-card' : m === 'pausar' ? 'pause' : 'log-out'} />
                                                        {m === 'metodo' ? 'Cambiar el método de pago' : m === 'pausar' ? 'Pausar la temporada' : 'Dar de baja el negocio'}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {tarjeta.datos.length > 0 && (
                        <div className="rv-plan__datos">
                            {tarjeta.datos.map((d) => (
                                <div key={d.clave}>
                                    <span className="rv-plan__sub">{d.clave}</span>
                                    <strong>{d.valor}</strong>
                                    {d.sub !== undefined && <span className={d.rojo === true ? 'rv-plan__rojo' : 'rv-plan__sub'}>{d.sub}</span>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {!permisos.puede('plan.contratar') && (
                    <p className="rv-plan__aviso">
                        <Icono nombre="eye" />
                        <span>Como Administrador ves el plan y el consumo. Contratar, pausar o dar de baja lo hace el propietario.</span>
                    </p>
                )}
                {!COBRO_DISPONIBLE && permisos.puede('plan.contratar') && (
                    <p className="rv-plan__aviso">
                        <Icono nombre="info" />
                        <span>{TEXTO_SIN_COBRO}</span>
                    </p>
                )}

                <section className="rv-plan__seccion">
                    <div className="rv-plan__titulo">
                        <h3>Lo que tienes</h3>
                        <span>{n === 0 ? 'La base' : `La base y ${String(n)} ${n === 1 ? 'bloque' : 'bloques'}`}</span>
                    </div>
                    <div className="rv-plan__bloques" role="list" aria-label="Lo que tienes">
                        {lista.map((b) => (
                            <div
                                key={b.product_code}
                                role="listitem"
                                className={`rv-plan__bloque${resaltado === b.product_code ? ' rv-plan__bloque--resaltado' : ''}`}
                                aria-current={resaltado === b.product_code ? 'true' : undefined}
                            >
                                <span className="rv-plan__baldosa">
                                    <Icono nombre={b.product_code === 'base' ? 'layers' : 'puzzle'} />
                                </span>
                                <span className="rv-plan__bloque-textos">
                                    <strong>{nombreBloque(b.product_code)}</strong>
                                    <span>{origenBloque(b)}</span>
                                </span>
                                {b.quantity > 1 && <span className="rv-plan__cantidad">{`× ${String(b.quantity)}`}</span>}
                            </div>
                        ))}
                    </div>
                    {resaltado !== null && !lista.some((b) => b.product_code === resaltado) && (
                        <p className="rv-plan__aviso rv-plan__aviso--resaltado" role="status">
                            <Icono nombre="plus" />
                            <span>{`${nombreBloque(resaltado)} no está en tu plan.`}</span>
                        </p>
                    )}
                </section>
            </>
        );
    })();

    const sub = datos?.suscripcion ?? null;
    const impagada = sub?.status === 'past_due' || sub?.status === 'suspended';
    const finPeriodo = sub?.current_period_end ?? null;
    const cerrar = () => {
        setVentana(null);
    };

    return (
        <div className="rv-plan">
            {segmentos}
            {contenido}

            <Ventana
                abierta={ventana === 'contratar'}
                titulo="Contratar el plan"
                texto="Pagas en la página segura de Stripe, con tarjeta o domiciliación SEPA, y vuelves aquí. Sin permanencia."
                accion="Ir al pago seguro"
                accionIcono="lock"
                ocupado={ocupado}
                ocupadoTexto="Abriendo el pago…"
                movil={movil}
                onCerrar={cerrar}
                onAccion={() => {
                    const items = (datos?.bloques.entitlements ?? [])
                        .filter((b) => b.source === 'trial' || b.source === 'subscription')
                        .map((b) => ({ product_code: b.product_code, quantity: b.quantity }));
                    hacer(() =>
                        ops.createSubscriptionCheckout(negocio, { items, return_url: `${window.location.origin}${ruta}` }).then((r) => {
                            irA(r.url);
                        }),
                    );
                }}
            />
            <Ventana
                abierta={ventana === 'metodo'}
                titulo={impagada ? 'Pagar y actualizar el método de pago' : 'Cambiar el método de pago'}
                texto={
                    impagada
                        ? 'Se abre la página segura de Stripe. Al guardar una tarjeta o una domiciliación SEPA válida se cobra lo pendiente.'
                        : 'Se abre la página segura de Stripe. Puedes poner otra tarjeta o una domiciliación SEPA.'
                }
                accion="Ir a la página segura"
                accionIcono="lock"
                ocupado={ocupado}
                ocupadoTexto="Abriendo…"
                movil={movil}
                onCerrar={cerrar}
                onAccion={() => {
                    hacer(() =>
                        ops.createBillingPortalSession(negocio).then((r) => {
                            irA(r.url);
                        }),
                    );
                }}
            />
            <Ventana
                abierta={ventana === 'pausar'}
                titulo="Pausar la temporada"
                texto="Guardamos tu configuración, el plano y el CRM. El panel queda en solo lectura y no entran reservas ni ventas hasta que reactives."
                accion="Pausar · 9 € al mes"
                accionIcono="pause"
                inactiva={vuelta === '' || inicio === ''}
                ocupado={ocupado}
                ocupadoTexto="Pausando…"
                movil={movil}
                onCerrar={cerrar}
                onAccion={() => {
                    hacer(() => ops.pauseSubscription(negocio, { starts_on: inicio, expected_return_on: vuelta }));
                }}
            >
                <label className="rv-plan__fecha">
                    <span>Empieza el</span>
                    <input
                        type="date"
                        value={inicio}
                        onChange={(e) => {
                            setInicio(e.target.value);
                        }}
                    />
                    <span className="rv-plan__sub">Desde el día siguiente a tu última reserva, cita o entrada. Hasta entonces sigues vendiendo.</span>
                </label>
                <label className="rv-plan__fecha">
                    <span>Vuelves el</span>
                    <input
                        type="date"
                        value={vuelta}
                        onChange={(e) => {
                            setVuelta(e.target.value);
                        }}
                    />
                    <span className="rv-plan__sub">Como mucho 9 meses. Ese día se reactiva sola; puedes reactivar antes.</span>
                </label>
            </Ventana>
            <Ventana
                abierta={ventana === 'compromisos'}
                titulo="Todavía no puedes pausar"
                texto={
                    pendientes?.desde === null || pendientes?.desde === undefined
                        ? 'Tienes reservas y entradas para fechas futuras. Con el negocio en pausa no se podrían atender: cancélalas o cambia su fecha, o espera a que pasen, y vuelve aquí.'
                        : `Tienes reservas y entradas para fechas futuras. Con el negocio en pausa no se podrían atender: la pausa puede empezar desde el ${dia(pendientes.desde)}.`
                }
                cancelar="Entendido"
                cancelarIcono="check"
                accion=""
                movil={movil}
                onCerrar={cerrar}
            >
                {pendientes !== null && pendientes.lineas.length > 0 && (
                    <span className="rv-plan__partes">
                        {pendientes.lineas.map((l) => (
                            <span key={l.clave} className="rv-plan__parte">
                                <span>{l.clave}</span>
                                {l.valor !== '' && <strong>{l.valor}</strong>}
                            </span>
                        ))}
                    </span>
                )}
            </Ventana>
            <Ventana
                abierta={ventana === 'reactivar'}
                titulo="Reactivar el negocio"
                texto="Vuelves a tu plan con los mismos bloques."
                accion="Reactivar"
                accionIcono="play"
                ocupado={ocupado}
                ocupadoTexto="Reactivando…"
                movil={movil}
                onCerrar={cerrar}
                onAccion={() => {
                    hacer(() => ops.resumeSubscription(negocio));
                }}
            />
            {/* G1176 (ADR-0268): anula la pausa que aún no ha empezado, sin coste; la suscripción sigue Activa como estaba. */}
            <Ventana
                abierta={ventana === 'cancelar-pausa'}
                titulo="Cancelar la pausa"
                texto={
                    sub?.pause?.starts_on === undefined
                        ? 'La pausa no llegará a empezar y tu plan sigue como está. No se cobra nada.'
                        : `La pausa no llegará a empezar el ${diaConAno(sub.pause.starts_on, new Date())} y tu plan sigue como está. No se cobra nada.`
                }
                cancelar="Seguir con la pausa"
                cancelarIcono="arrow-left"
                accion="Cancelar la pausa"
                accionIcono="x"
                ocupado={ocupado}
                ocupadoTexto="Cancelando…"
                movil={movil}
                onCerrar={cerrar}
                onAccion={() => {
                    ejecutar(() => ops.resumeSubscription(negocio), 'pausa-cancelada');
                }}
            />
            <Ventana
                abierta={ventana === 'pausa-cancelada'}
                titulo="Pausa cancelada"
                texto="Tu plan sigue como estaba. No se ha cobrado nada."
                cancelar=""
                accion="Hecho"
                accionIcono="check"
                movil={movil}
                onCerrar={cerrar}
                onAccion={cerrar}
            />
            {/* Qué pasa a los 30 días de la baja (bloqueo de lo legal y anonimización): «Texto a validar» con la asesoría (G198, G204, G242). */}
            <Ventana
                abierta={ventana === 'baja'}
                titulo="¿Dar de baja el negocio?"
                texto={
                    finPeriodo === null
                        ? 'La baja es efectiva al final del periodo pagado. Antes puedes descargar todos tus datos.'
                        : `La baja es efectiva el ${dia(finPeriodo)}, al final del periodo pagado. Antes puedes descargar todos tus datos.`
                }
                cancelar="Seguir"
                cancelarIcono="arrow-left"
                accion="Dar de baja"
                accionIcono="log-out"
                ocupado={ocupado}
                ocupadoTexto="Dando de baja…"
                movil={movil}
                onCerrar={cerrar}
                onAccion={() => {
                    hacer(() => conDosPasosEnElMomento(ops, pedirCodigo, (clave) => ops.scheduleCancellation(negocio, clave)));
                }}
            >
                <div className="rv-plan-baja">
                    <Chip estado="pending">Texto a validar</Chip>
                    <div className="rv-ca-dato">
                        <span>{finPeriodo === null ? 'Hasta el fin del periodo' : `Hasta el ${dia(finPeriodo)}`}</span>
                        <strong>Todo funciona igual</strong>
                    </div>
                    <div className="rv-ca-dato">
                        <span>Durante 30 días</span>
                        <strong>Solo lectura: puedes entrar y descargar tus datos</strong>
                    </div>
                    <div className="rv-ca-dato">
                        <span>{finPeriodo === null ? 'Pasados los 30 días' : `Desde el ${diaMasDias(finPeriodo, 31)}`}</span>
                        <strong>Guardamos bloqueado lo que obliga la ley, como facturas y pagos, y anonimizamos el resto</strong>
                    </div>
                    <div className="rv-ca-dato">
                        <span>Tus clientes</span>
                        <strong>Sus Cuentas Reservaria no se tocan: solo dejan de estar ligadas a tu negocio</strong>
                    </div>
                </div>
                {pausa && (
                    <div className="rv-plan__alternativa">
                        <span>¿Es por temporada? La pausa cuesta 9 € al mes y lo guarda todo.</span>
                        <Boton
                            tipo="secundario"
                            icono="pause"
                            onClick={() => {
                                setVentana('pausar');
                            }}
                        >
                            Pausar en su lugar
                        </Boton>
                    </div>
                )}
            </Ventana>
        </div>
    );
}

/** Arco del consumo (1.16): porcentaje grande en el centro; rojo desde el 90 %. */
function Anillo({ porcentaje, alerta }: { porcentaje: number; alerta: boolean }) {
    const r = 42;
    const largo = 2 * Math.PI * r;
    return (
        <span className="rv-plan__anillo" aria-hidden="true">
            <svg viewBox="0 0 100 100" width="96" height="96">
                <circle cx="50" cy="50" r={r} fill="none" stroke="var(--rv-paper)" strokeWidth="8" />
                <circle
                    cx="50"
                    cy="50"
                    r={r}
                    fill="none"
                    stroke={alerta ? 'var(--rv-red-dark)' : 'var(--rv-fg-3)'}
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeDasharray={`${String((largo * porcentaje) / 100)} ${String(largo)}`}
                    transform="rotate(-90 50 50)"
                />
            </svg>
            <span className={alerta ? 'rv-plan__rojo' : undefined}>
                <strong>{porcentaje}</strong>
                <small>%</small>
            </span>
        </span>
    );
}

/** Hoy en la hora del navegador, como `2026-11-01` (el valor de un `<input type="date">`). */
function hoyLocal(): string {
    const d = new Date();
    return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
